import { captureException, withScope } from '@sentry/react';
import { z } from 'zod';

import { sentryState } from '@/store/SearchStore';

const API_KEY = String(import.meta.env.VITE_CERC_API_KEY);

const COLORS = {
  green: '#00B41F',
  yellow: '#FFD420',
  red: '#E56363',
};

const COLORS_DARK = {
  green: '#00851F',
  yellow: '#A88700',
  red: '#D70D0D',
};

const COLORS_NO_AQ = {
  unselected: '#7378ff',
  selected: '#262DFF',
};

const COLORS_NO_AQ_DARK = {
  unselected: '#262DFF',
  selected: '#7378FF',
};

const zCercJourney = z
  .object({
    polyline_id: z.string(),
    percentage_above_lowest: z.number().nullable(),
    no2: z.number().nullable(),
    'pm2.5': z.number().nullable(),
    route_ranking: z.number().nullable(),
    in_modelled_area: z.enum(['Y', 'N', 'Partial']),
  })
  .array();

export type CercJourney = z.infer<typeof zCercJourney>;

export type RouteAirQuality = NonNullable<Awaited<ReturnType<typeof getAirQuality>>>[0];

type CacheEntry<T> = {
  data: T[] | undefined;
  expiry: number;
};

const airQualityCache = new Map<string, CacheEntry<RouteAirQuality>>();

const CACHE_EXPIRATION = 5 * 60 * 1000; // 5 min

export async function getAirQuality(journeyStartTime: Date | undefined, routes: google.maps.DirectionsRoute[]) {
  const cacheKey = JSON.stringify({
    time: journeyStartTime?.getTime(),
    routes: routes.map((route) => route.overview_polyline),
  });

  const cachedEntry = airQualityCache.get(cacheKey);
  const now = Date.now();

  if (cachedEntry && cachedEntry.expiry > now) {
    const cachedResult = cachedEntry.data as typeof airQuality;
    return cachedResult;
  }

  const isoWithoutMs =
    (journeyStartTime || new Date(new Date().getTime() + 60000 * 2)).toISOString().substring(0, 19) + 'Z';

  const url = `/getexposure?journey_start_time=${isoWithoutMs}`;
  const body = { routes: mapToCercRoutes(routes) };

  let response: Response | undefined;
  let fetchError: Error | undefined;

  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify(body),
    });
  } catch (error) {
    fetchError = error instanceof Error ? error : new Error(String(error));
  }

  if (!response || !response.ok) {
    const errorBody = response ? await response.text() : undefined;

    withScope((scope) => {
      scope.setContext('searchState', sentryState());
      scope.setContext('searchResponse', {
        status: response?.status,
        statusText: response?.statusText,
        body: errorBody,
      });
      if (fetchError) {
        scope.setContext('fetchError', {
          name: fetchError.name,
          message: fetchError.message,
        });
      }
      captureException(new Error('Unable to get air quality data from CERC API'));
    });
    return undefined;
  }

  const journey = zCercJourney.parse(await response.json());
  const airQuality = journey.map((j) => {
    const googleRoute = routes.find((r) => r.overview_polyline === j.polyline_id)?.legs[0];
    const hasAirQualityData = j.in_modelled_area === 'Y';

    if (!hasAirQualityData) {
      return {
        ...j,
        color: COLORS_NO_AQ.unselected,
        stroke: COLORS_NO_AQ_DARK.unselected,
        label_ranking: 'No air quality data',
        label_distance: googleRoute?.distance?.text,
        label_duration: googleRoute?.duration?.text,
        percentage_bellow_highest: null,
      };
    }

    const lastRoute = journey[journey.length - 1];
    return {
      ...j,
      color:
        j.route_ranking === 1
          ? COLORS.green
          : j.route_ranking === 2 && journey.length === 3
            ? COLORS.yellow
            : COLORS.red,
      stroke:
        j.route_ranking === 1
          ? COLORS_DARK.green
          : j.route_ranking === 2 && journey.length === 3
            ? COLORS_DARK.yellow
            : COLORS_DARK.red,
      label_ranking:
        j.route_ranking === 1 ? 'Lowest' : j.route_ranking === 2 && journey.length === 3 ? 'Middle' : 'Highest',
      label_distance: googleRoute?.distance?.text,
      label_duration: googleRoute?.duration?.text,
      percentage_bellow_highest:
        j.no2 !== null && j['pm2.5'] !== null && lastRoute.no2 !== null && lastRoute['pm2.5'] !== null
          ? {
              no2: Math.round(((j.no2 - lastRoute.no2) / lastRoute.no2) * 100),
              pm2_5: Math.round(((j['pm2.5'] - lastRoute['pm2.5']) / lastRoute['pm2.5']) * 100),
            }
          : null,
    };
  });

  airQualityCache.set(cacheKey, {
    data: airQuality,
    expiry: now + CACHE_EXPIRATION,
  });

  return airQuality;
}

const mapToCercRoutes = (routes: google.maps.DirectionsRoute[]) =>
  routes.map((route) => ({
    distanceMeters: route.legs.reduce((acc, leg) => acc + (leg.distance?.value || 0), 0),
    duration: route.legs.reduce((acc, leg) => acc + (leg.duration?.value || 0), 0).toString(),
    polyline: {
      geoJsonLinestring: {
        coordinates: route.overview_path.map((pt) => [pt.lng(), pt.lat()]),
      },
    },
    description: route.overview_polyline,
  }));
